import { CollectionResponse } from '@bynder/collection-picker';
import { useTranslate } from '@bynder/localization';
import { GUIDE_TITLE_MAX_LENGTH } from '../constants/guides';
import { useCreateGroup, useCreateGuideInsideGroup } from '../mutations/groups';
import {
  useCreateGuide,
  useDuplicateGuide,
  useDuplicateGuideInsideGroup,
  useGuideMutations,
  useRemoveGuideFromGroup,
} from '../mutations/guides';
import { GuideType, ModalType } from '../types';
import { generateUUID } from '../helpers/utils';
import { API_ROUTES } from '../constants/app';
import { track } from '../helpers/analytics';
import { EntityEnum } from '../components/modals/NewCreateModal';
import { useModalStore } from '../stores/modalStore';
import { copyShareUrlToClipboard } from '../constants/widgets';
import { LinkCopiedToClipboardNotification } from '../types/notifications';
import { notify } from '@bynder/design-system';
import { useOverviewStore } from '../stores/overviewStore';
import { usePageMutations } from '../mutations/pages';

export const useSectionTemplateModal = () => {
  const openModal = useModalStore.use.openModal();

  return () =>
    openModal({
      modalType: ModalType.SECTION_TEMPLATE_MODAL,
      modalOnSubmit: () => {},
      modalProps: {},
    });
};

export const usePageTemplateModal = () => {
  const openModal = useModalStore.use.openModal();

  return ({
    chapterId,
    toIndex,
    entityType,
  }: {
    chapterId?: string;
    toIndex?: number;
    entityType: EntityEnum;
  }) =>
    openModal({
      modalType: ModalType.PAGE_TEMPLATE_MODAL,
      modalOnSubmit: () => {},
      modalProps: {
        chapterId,
        toIndex,
        entityType,
      },
    });
};

export const useCreateModal = () => {
  const activeGroupId = useOverviewStore.use.activeGroupId();
  const createGuide = useCreateGuide();
  const createGuideInsideGroup = useCreateGuideInsideGroup(activeGroupId!);
  const createGroup = useCreateGroup();
  const { translate } = useTranslate();
  const openModal = useModalStore.use.openModal();

  const onCreateEntity = (
    title: string,
    type: EntityEnum,
    themesetId?: string,
  ) => {
    if (type === EntityEnum.GUIDE) {
      createGuide.mutate({ guideTitle: title, themesetId });
    } else {
      createGroup.mutate({ groupTitle: title });
    }
  };

  const onCreateEntityInsideGroup = (
    title: string,
    _type: EntityEnum,
    themesetId?: string,
  ) => {
    createGuideInsideGroup.mutate({ guideTitle: title, themesetId });
  };

  return (entityType: EntityEnum) =>
    openModal({
      modalType: ModalType.NEW_CREATE_MODAL,
      modalOnSubmit: activeGroupId ? onCreateEntityInsideGroup : onCreateEntity,
      modalProps: {
        entityType,
        headerText: translate('CREATE_GUIDE_MODAL.HEADER_TEXT'),
        labelText: translate('CREATE_GUIDE_MODAL.LABEL_TEXT'),
        placeholderText: translate('CREATE_GUIDE_MODAL.PLACEHOLDER_TEXT'),
        maxLength: GUIDE_TITLE_MAX_LENGTH,
      },
    });
};

const getEditTitleHeaderText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'EDIT_GUIDE_MODAL.HEADER_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'EDIT_GUIDE_MODAL.HEADER_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'EDIT_GUIDE_MODAL.HEADER_TEXT';
  }
};
const getEditTitleLabelText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'EDIT_GUIDE_MODAL.LABEL_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'EDIT_GUIDE_MODAL.LABEL_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'EDIT_GUIDE_MODAL.LABEL_TEXT';
  }
};
const getEditTitlePlaceholderText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'EDIT_GUIDE_MODAL.PLACEHOLDER_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'EDIT_GUIDE_MODAL.PLACEHOLDER_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'EDIT_GUIDE_MODAL.PLACEHOLDER_TEXT';
  }
};
export const useEditGuideTitleModal = (
  guideTitle: string,
  guideId: string,
  guideType: GuideType,
) => {
  const { translate } = useTranslate();
  const openModal = useModalStore.use.openModal();
  const { editGuideTitle: editGuideTitleMutation } = useGuideMutations(guideId);

  const onEditGuideTitle = (title: string) => {
    track('Guide Edited');

    editGuideTitleMutation.mutate({
      guideTitle: title,
    });
  };

  const modalProps = {
    initialValue: guideTitle,
    headerText: translate(getEditTitleHeaderText(guideType)),
    labelText: translate(getEditTitleLabelText(guideType)),
    placeholderText: translate(getEditTitlePlaceholderText(guideType)),
    buttonLabel: translate('EDIT_MODAL.BUTTON_SAVE'),
    maxLength: GUIDE_TITLE_MAX_LENGTH,
  };

  return () =>
    openModal({
      modalType: ModalType.CREATE_MODAL,
      modalOnSubmit: onEditGuideTitle,
      modalProps,
    });
};
export const useEditPageTitleModal = (pageTitle: string, pageId: string) => {
  const { translate } = useTranslate();
  const openModal = useModalStore.use.openModal();
  const { editPageTitle } = usePageMutations();

  const onEditPageTitle = (title: string) => {
    track('PAge Edited');

    editPageTitle.mutate({
      newTitle: title,
      pageId,
    });
  };
  const modalProps = {
    initialValue: pageTitle,
    headerText: translate('EDIT_PAGE_MODAL.HEADER_TEXT'),
    labelText: translate('EDIT_PAGE_MODAL.LABEL_TEXT'),
    placeholderText: translate('EDIT_PAGE_MODAL.PLACEHOLDER_TEXT'),
    buttonLabel: translate('EDIT_MODAL.BUTTON_SAVE'),
    maxLength: GUIDE_TITLE_MAX_LENGTH,
  };

  return () =>
    openModal({
      modalType: ModalType.CREATE_MODAL,
      modalOnSubmit: onEditPageTitle,
      modalProps,
    });
};

export const useAudiencesModal = () => {
  const openModal = useModalStore.use.openModal();

  return () =>
    openModal({
      modalType: ModalType.AUDIENCES_MODAL,
      modalOnSubmit: () => {},
      modalProps: {},
    });
};

export const useTargetedPagesModal = () => {
  const openModal = useModalStore.use.openModal();

  return () =>
    openModal({
      modalType: ModalType.TARGETED_PAGES_MODAL,
      modalOnSubmit: () => {},
      modalProps: {},
    });
};

const getDuplicateHeaderText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.HEADER_TEXT_HOME_PAGE';
    case GuideType.SINGLE_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.HEADER_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DUPLICATE_GUIDE_MODAL.HEADER_TEXT';
  }
};
const getDuplicateLabelText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.LABEL_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.LABEL_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DUPLICATE_GUIDE_MODAL.LABEL_TEXT';
  }
};
const getDuplicatePlaceholderText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.PLACEHOLDER_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.PLACEHOLDER_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DUPLICATE_GUIDE_MODAL.PLACEHOLDER_TEXT';
  }
};
const getDuplicateButtonText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.BUTTON_SAVE_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'DUPLICATE_GUIDE_MODAL.BUTTON_SAVE_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DUPLICATE_GUIDE_MODAL.BUTTON_SAVE';
  }
};
export const useDuplicateGuideModal = (
  sourceGuideId: string,
  guideTitle: string,
  groupId: string | null,
  guideType?: GuideType,
) => {
  const { translate } = useTranslate();
  const openModal = useModalStore.use.openModal();
  const copyText = translate('DUPLICATE_GUIDE_MODAL.COPY');
  const duplicateGuide = useDuplicateGuide(sourceGuideId, guideType!);
  const duplicateGuideInsideGroup = useDuplicateGuideInsideGroup(
    sourceGuideId,
    groupId!,
  );
  const newGuideId = generateUUID();

  const modalProps = {
    initialValue: `${guideTitle.substring(
      0,
      GUIDE_TITLE_MAX_LENGTH - copyText.length,
    )}${copyText}`,
    headerText: translate(getDuplicateHeaderText(guideType!)),
    labelText: translate(getDuplicateLabelText(guideType!)),
    placeholderText: translate(getDuplicatePlaceholderText(guideType!)),
    buttonLabel: translate(getDuplicateButtonText(guideType!)),
    maxLength: GUIDE_TITLE_MAX_LENGTH,
  };

  const modalOnSubmit = (newTitle: string) =>
    groupId
      ? duplicateGuideInsideGroup.mutate({ newTitle, newGuideId })
      : duplicateGuide.mutate({ newTitle, newGuideId });

  return () =>
    openModal({ modalType: ModalType.CREATE_MODAL, modalOnSubmit, modalProps });
};

export const useAddCollectionWidgetModal = (
  onAddWidget: (collection: CollectionResponse) => void,
) => {
  const openModal = useModalStore.use.openModal();

  const modalProps = {
    collectionsApiUrl: API_ROUTES.GRAPHQL,
  };

  return () =>
    openModal({
      modalType: ModalType.COLLECTIONS_MODAL,
      modalOnSubmit: onAddWidget,
      modalProps,
    });
};

export const usePermissionsModal = (
  entityId: string,
  guideId: string,
  entityTitle: string,
) => {
  const openModal = useModalStore.use.openModal();
  const { translate } = useTranslate();

  const handleShareClick = () => {
    copyShareUrlToClipboard(guideId, entityId)
      .then(() => notify(LinkCopiedToClipboardNotification))
      .catch(e => {
        throw new Error(e);
      });
  };

  const modalProps = {
    entityId,
    activeGuideId: guideId,
    headerText: translate('ACCESS_MODAL.HEADER_TEXT', {
      title: entityTitle,
    }),
    onCopyUrl: handleShareClick,
  };
  return () =>
    openModal({
      modalType: ModalType.ACCESS_MODAL,
      modalOnSubmit: () => null,
      modalProps,
    });
};

const getDeleteHeaderText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'DELETE_GUIDE_MODAL.HEADER_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'DELETE_GUIDE_MODAL.HEADER_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DELETE_GUIDE_MODAL.HEADER_TEXT';
  }
};
const getDeleteContentText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
    case GuideType.SINGLE_PAGE:
      return 'DELETE_GUIDE_MODAL.CONTENT_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DELETE_GUIDE_MODAL.CONTENT_TEXT';
  }
};
const getDeleteButtonText = (guideType: GuideType) => {
  switch (guideType) {
    case GuideType.HOME_PAGE:
      return 'DELETE_GUIDE_MODAL.BUTTON_TEXT_HOMEPAGE';
    case GuideType.SINGLE_PAGE:
      return 'DELETE_GUIDE_MODAL.BUTTON_TEXT_PAGE';
    case GuideType.MULTI_PAGE:
    default:
      return 'DELETE_GUIDE_MODAL.BUTTON_TEXT';
  }
};
export const useDeleteGuideModal = (
  guideId: string,
  guideTitle: string,
  groupId: string,
  guideType: GuideType,
) => {
  const { translate } = useTranslate();
  const openModal = useModalStore.use.openModal();
  const removeGuideFromGroup = useRemoveGuideFromGroup(guideId, groupId);
  const { deleteGuide } = useGuideMutations(guideId);

  const onDeleteGuide = () => {
    if (groupId) {
      removeGuideFromGroup.mutate();
    } else {
      deleteGuide.mutate({ guideId });
    }
  };

  const modalProps = {
    headerText: translate(getDeleteHeaderText(guideType)),
    checkbox1Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_1_TEXT'),
    checkbox2Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_2_TEXT'),
    contentText: translate(getDeleteContentText(guideType), {
      title: guideTitle,
    }),
    buttonText: translate(getDeleteButtonText(guideType)),
  };

  return () =>
    openModal({
      modalType: ModalType.DELETE_MODAL,
      modalOnSubmit: onDeleteGuide,
      modalProps,
    });
};
